/**
 * ReviewPrompt — shown after Close phase completes on session 3+.
 * Submits to public.nixxtech_reviews with status='pending'.
 * Suppressed for 30 days after dismiss, permanently if opted out.
 *
 * nixxtech_reviews lives in the PUBLIC schema, not `inner_awakening` — it's
 * shared across NixxTech products (see supabase/migrations). Insert through
 * the plain `supabase` client, never `db()` (which is schema-scoped to
 * `inner_awakening` and 404s: PGRST205 "table not found").
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../lib/supabase'
import type { User } from '@supabase/supabase-js'

const SUPPRESS_KEY = 'ia_review_suppress_until'
const OPT_OUT_KEY  = 'ia_review_opted_out'

export function shouldShowReview(completedCount: number): boolean {
  if (completedCount < 3) return false
  if (localStorage.getItem(OPT_OUT_KEY)) return false
  const suppress = localStorage.getItem(SUPPRESS_KEY)
  if (suppress && Date.now() < Number(suppress)) return false
  return true
}

function suppress30Days() {
  localStorage.setItem(SUPPRESS_KEY, String(Date.now() + 30 * 24 * 60 * 60 * 1000))
}
function optOut() {
  localStorage.setItem(OPT_OUT_KEY, '1')
}

interface Props {
  user: User
  tier: string
  sessionCount: number
  onDismiss: () => void
}

export default function ReviewPrompt({ user, tier, sessionCount, onDismiss }: Props) {
  const { t } = useTranslation()
  const [rating, setRating]           = useState(0)
  const [hoverRating, setHoverRating] = useState(0)
  const [quote, setQuote]             = useState('')
  const [displayName, setDisplayName] = useState(user.email?.split('@')[0] ?? 'Anonymous')
  const [submitted, setSubmitted]     = useState(false)
  const [submitting, setSubmitting]   = useState(false)
  const [error, setError]             = useState('')

  async function submit() {
    if (!quote.trim() || quote.length < 20) { setError(t('review.minLength')); return }
    if (!supabase) { setError(t('errors.notConfigured')); return }
    setSubmitting(true)
    const { error: err } = await supabase
      .from('nixxtech_reviews')
      .insert({
        app:          'inner_awakening',
        user_id:      user.id,
        rating:       rating || null,
        quote:        quote.trim(),
        display_name: displayName.trim() || 'Anonymous',
        context:      `${tier === 'free' ? 'Free tier' : tier.charAt(0).toUpperCase() + tier.slice(1)} member · ${sessionCount} session${sessionCount !== 1 ? 's' : ''}`,
        tier,
        session_count: sessionCount,
        status:       'pending',
      })
    setSubmitting(false)
    if (err) { setError(t('errors.generic')); return }
    setSubmitted(true)
    optOut()
    setTimeout(onDismiss, 2200)
  }

  function dismiss(permanent = false) {
    if (permanent) optOut()
    else suppress30Days()
    onDismiss()
  }

  const overlay: React.CSSProperties = {
    position: 'fixed', inset: 0, zIndex: 9999,
    background: 'rgba(28,17,40,0.65)', backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    padding: '1rem',
  }
  const card: React.CSSProperties = {
    width: '100%', maxWidth: '28rem',
    background: 'white', borderRadius: '1.5rem',
    padding: '2rem', boxShadow: '0 24px 64px rgba(28,17,40,0.25)',
    fontFamily: 'var(--font-inter, system-ui, sans-serif)',
  }

  if (submitted) {
    return (
      <div style={overlay}>
        <div style={{ ...card, textAlign: 'center' }}>
          <p style={{ fontSize: '1.5rem', margin: '0 0 0.75rem' }}>✦</p>
          <p style={{ fontFamily: 'Georgia, serif', fontSize: '1.125rem', color: '#1C1128', margin: '0 0 0.5rem' }}>{t('review.thankYou')}</p>
          <p style={{ fontSize: '0.875rem', color: '#6B5F7A', margin: 0 }}>{t('review.wordsHelp')}</p>
        </div>
      </div>
    )
  }

  return (
    <div style={overlay} onClick={e => { if (e.target === e.currentTarget) dismiss() }}>
      <div style={card}>
        <p style={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#9B72C0', margin: '0 0 0.75rem' }}>
          {t('review.quickMoment')}
        </p>
        <p style={{ fontFamily: 'Georgia, serif', fontSize: '1.125rem', color: '#1C1128', lineHeight: 1.5, margin: '0 0 1.5rem' }}>
          {t('review.wouldYouShare')}
        </p>
        <p style={{ fontSize: '0.8125rem', color: '#9B8FAA', margin: '0 0 1.5rem' }}>
          {t('review.mayHelpSomeone')}
        </p>

        {/* Stars */}
        <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.25rem' }}>
          {[1,2,3,4,5].map(n => (
            <button
              key={n}
              onClick={() => setRating(n)}
              onMouseEnter={() => setHoverRating(n)}
              onMouseLeave={() => setHoverRating(0)}
              style={{ fontSize: '1.5rem', background: 'none', border: 'none', cursor: 'pointer', color: n <= (hoverRating || rating) ? '#B87D0A' : '#E4D5EE', padding: '0.125rem', lineHeight: 1 }}
            >
              ★
            </button>
          ))}
          {rating > 0 && <span style={{ fontSize: '0.8125rem', color: '#9B8FAA', alignSelf: 'center', marginLeft: '0.5rem' }}>{t(`review.ratings.${rating}`)}</span>}
        </div>

        {/* Quote */}
        <textarea
          value={quote}
          onChange={e => { setQuote(e.target.value); setError('') }}
          placeholder={t('review.placeholder')}
          rows={3}
          style={{ width: '100%', boxSizing: 'border-box', padding: '0.75rem 1rem', borderRadius: '0.75rem', border: '1.5px solid #E4D5EE', fontSize: '0.9rem', color: '#1C1128', lineHeight: 1.65, resize: 'vertical', outline: 'none', fontFamily: 'inherit' }}
        />
        {error && <p style={{ fontSize: '0.8rem', color: '#C45272', margin: '0.375rem 0 0' }}>{error}</p>}

        {/* Display name */}
        <div style={{ marginTop: '0.875rem' }}>
          <label style={{ fontSize: '0.75rem', color: '#9B8FAA', display: 'block', marginBottom: '0.375rem' }}>
            {t('review.yourNamePublic')}
          </label>
          <input
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            placeholder={t('review.namePlaceholder')}
            style={{ width: '100%', boxSizing: 'border-box', padding: '0.625rem 0.875rem', borderRadius: '0.625rem', border: '1.5px solid #E4D5EE', fontSize: '0.875rem', color: '#1C1128', outline: 'none', fontFamily: 'inherit' }}
          />
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '0.625rem', marginTop: '1.25rem' }}>
          <button
            onClick={submit}
            disabled={submitting}
            style={{ flex: 1, padding: '0.75rem', borderRadius: '0.75rem', background: '#3D1F62', color: 'white', fontSize: '0.9375rem', fontWeight: 500, border: 'none', cursor: 'pointer', opacity: submitting ? 0.7 : 1 }}
          >
            {submitting ? t('review.sending') : t('review.share')}
          </button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.875rem' }}>
          <button onClick={() => dismiss(false)} style={{ fontSize: '0.8125rem', color: '#9B8FAA', background: 'none', border: 'none', cursor: 'pointer' }}>
            {t('review.maybeLater')}
          </button>
          <button onClick={() => dismiss(true)} style={{ fontSize: '0.8125rem', color: '#C4B5D4', background: 'none', border: 'none', cursor: 'pointer' }}>
            {t('review.noThanks')}
          </button>
        </div>
      </div>
    </div>
  )
}
